import React, { useState, useEffect, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { Row, Col, Space, message, Select } from 'antd'
import { SearchOutlined, QrcodeOutlined, CloseOutlined } from '@ant-design/icons'

// Import custom components
import Button from '../../components/common/button/button'
import Modal from '../../components/common/modal/modal'
import Input from '../../components/common/input/input'
import Typography from '../../components/common/typography/typography'
import Spinner from '../../components/common/spinner/spinner'
import ProductCard from '../../components/feature/productCard/productCard'
import VideoCapture from '../../components/barcodeScanner/BarcodeScanner'
// Import utility functions
import { convertToCommaSeparatedKeycodeUtil } from '../../utils/functionUtils'
import { handleProntoProductsUtil } from '../../api/productLookupApiService/productLookupApiUtils'
import { detectCognitoUser } from '../shelvesEdgeLabelling/shelvesEdgeLabellingUtils'
import { STORE_MAPPING, getStoreNameById } from '../../utils/storeMapping'

// Import assets and utilities
import NoDataImage from '../../assets/images/noScanimage.jpg'

// Import styles
import './ProductLookup.scss'

const { Title, Text } = Typography
const EXCLUDED_PRODUCT_LOOKUP_STORE_IDS = [1, 107, 108, 109]

const isSelectableProductLookupStore = (storeId) => {
  if (storeId === null || storeId === undefined || storeId === '') {
    return false
  }

  const parsedStoreId = Number(storeId)
  return (
    Number.isInteger(parsedStoreId) &&
    Object.prototype.hasOwnProperty.call(STORE_MAPPING, parsedStoreId) &&
    !EXCLUDED_PRODUCT_LOOKUP_STORE_IDS.includes(parsedStoreId)
  )
}

const ProductLookup = () => {
  const [productData, setProductData] = useState(null)
  const [loadingSOH, setLoadingSOH] = useState(false)
  const [dialogError, setDialogError] = useState('')
  const [isScannerActive, setIsScannerActive] = useState(false)
  const [isReloading, setIsReloading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [keycodeInput, setKeycodeInput] = useState('')
  const { instance } = useMsal()
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700)
  const [isCognitoUser, setIsCognitoUser] = useState(false)
  const [searchStoreId, setSearchStoreId] = useState(() => {
    try {
      const savedStore = localStorage.getItem('selectedStore')
      const id = savedStore ? Number(JSON.parse(savedStore)?.id) : null
      return isSelectableProductLookupStore(id) ? id : null
    } catch {
      return null
    }
  })
  const lastSearchRef = useRef(null) // tracks { values, searchType } of the most recent search
  const latestRequestIdRef = useRef(0)

  const storeOptions = Object.entries(STORE_MAPPING)
    .filter(([id]) => isSelectableProductLookupStore(id))
    .map(([id, name]) => ({
      value: parseInt(id),
      label: name
    }))

  // Re-fetch when the store selector changes (only if a search has already been performed)
  useEffect(() => {
    if (searchStoreId !== null && lastSearchRef.current) {
      const { values, searchType } = lastSearchRef.current
      handleDataFetch(values, searchType, searchStoreId)
    }
  }, [searchStoreId])

  // Detect authentication type
  useEffect(() => {
    const checkAuthType = async () => {
      try {
        const cognitoUser = await detectCognitoUser(instance)
        setIsCognitoUser(cognitoUser)
      } catch (error) {
        console.error('Error detecting auth type:', error)
        setIsCognitoUser(false)
      }
    }

    checkAuthType()
  }, [instance])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 700)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleModalClose = () => {
    setModalVisible(false)
    setKeycodeInput('')
    setDialogError('')
  }
  const validateAPN = (apn) => {
    // Regex is used to strip out all non-digit characters from APN to ensure only numbers are validated
    const cleanAPN = apn.replace(/[^\d]/g, '')
    return cleanAPN.length === 13
  }

  const validateKeycode = (keycode) => {
    // Regex is used to strip out all non-digit characters from keycode
    const cleanKeycode = keycode.replace(/[^\d]/g, '')
    return cleanKeycode.length <= 10
  }

  const handleDataFetch = async (values, searchType, storeId) => {
    const requestId = ++latestRequestIdRef.current

    // Remember the last search so we can re-run it when the store changes.
    lastSearchRef.current = { values, searchType }

    if (!isSelectableProductLookupStore(storeId)) {
      setProductData(null)
      message.warning({
        content: 'Please select a store to search SOH.',
        duration: 3
      })
      return
    }

    try {
      setIsReloading(true)
      setLoadingSOH(true)

      // Pass the isCognitoUser flag and storeId to handleProntoProductsUtil
      const response = await handleProntoProductsUtil(
        instance,
        values,
        searchType,
        isCognitoUser,
        Number(storeId)
      )

      // Ignore stale responses from earlier requests.
      if (requestId !== latestRequestIdRef.current) {
        return
      }

      if (response?.status === 200 && response.data?.sohInfo?.length > 0) {
        // Removed warning message for missing keycodes
        const formattedData = response.data.sohInfo.map((item) => ({
          price: item.ItemPrices?.ItemPrice?.RecRetailIncPrice,
          description: item.ItemDescription,
          department: item.ReorderBuyerCode,
          keycode: item.ItemCode,
          apn: item.APN,
          status: item.ABCClassCode,
          dcSOH: item.dcSOH,
          storeSOH: item.storeSOH,
          inTransitQty: item.inTransitQty,
          imageUrl: item.imageUrl
        }))
        setProductData(formattedData)
      } else {
        setProductData(null)
        message.error({
          content: 'No data available',
          duration: 3
        })
      }
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) {
        return
      }

      console.error('Error in handleDataFetch:', error)
      setProductData(null)
    } finally {
      if (requestId !== latestRequestIdRef.current) {
        return
      }

      setLoadingSOH(false)
      setIsReloading(false)
    }
  }

  const handleBarcodeScannedWrapper = async (barcodeResult) => {
    if (barcodeResult?.length) {
      const barcodeText = barcodeResult[0].text
      if (!validateAPN(barcodeText)) {
        setIsScannerActive(true)
        return false
      }
      await handleDataFetch([parseInt(barcodeText, 10)], 'apn', searchStoreId)
      setIsScannerActive(false)
      return true
    } else {
      return false
    }
  }

  const handleKeycodeChange = (e) => {
    const inputText = e.target.value
    // Remove alphabetic characters
    const sanitizedInput = inputText.replace(/[a-zA-Z]/g, '')
    setKeycodeInput(sanitizedInput)

    // Normalize input for validation
    const normalizedInput = sanitizedInput.replace(/[\r\n,\s]+/g, ',').replace(/^,|,$/g, '')

    const keycodes = normalizedInput.split(',').filter((k) => k.length > 0)
    const uniqueKeycodes = [...new Set(keycodes)]

    let error = ''

    if (keycodes.length !== uniqueKeycodes.length) {
      error = 'Duplicate keycodes are not allowed.'
      // Update input to show only unique keycodes
      setKeycodeInput(uniqueKeycodes.join(', '))
    } else if (keycodes.length > 4) {
      error = 'Maximum 4 keycodes are allowed.'
    } else if (keycodes.some((k) => !validateKeycode(k))) {
      error = 'Keycodes must not exceed 10 digits.'
    }

    setDialogError(error)
  }

  const handleSearchKeycode = async () => {
    if (!keycodeInput.trim()) {
      setDialogError('Please enter keycodes before searching.')
      return
    }

    // Process input allowing spaces and commas
    const sanitizedInput = keycodeInput.replace(/[\r\n,\s]+/g, ',').replace(/^,|,$/g, '')

    const keycodes = sanitizedInput.split(',').filter((k) => k.length > 0)

    if (keycodes.length > 4) {
      setDialogError('Maximum 4 keycodes are allowed.')
      return
    }

    const hasInvalidKeycode = keycodes.some((k) => !validateKeycode(k))

    if (hasInvalidKeycode) {
      setDialogError('Keycodes must not exceed 10 digits.')
      return
    }

    // Important: Use the utility directly like in the old code
    const processedKeycodes = convertToCommaSeparatedKeycodeUtil(sanitizedInput)

    if (processedKeycodes.length === 0) {
      setDialogError('Please enter valid keycodes.')
      return
    }

    handleModalClose()
    // Convert to integers and pass to data fetch function
    await handleDataFetch(
      processedKeycodes.map((k) => parseInt(k, 10)),
      'keycode',
      searchStoreId
    )
  }

  if (isScannerActive) {
    return (
      <div className="product-lookup-scanner-container">
        <div className="product-lookup-scanner-button-wrapper">
          <Button
            type="primary"
            danger
            icon={<CloseOutlined />}
            onClick={() => setIsScannerActive(false)}
            className="product-lookup-scanner-cancel"
          >
            Cancel
          </Button>
        </div>
        <div className="product-lookup-scanner">
          <VideoCapture onBarcodeScanned={handleBarcodeScannedWrapper} />
        </div>
      </div>
    )
  }

  return (
    <div className="product-lookup">
      <Row className="product-lookup-header">
        <Col span={24}>
          <Title level={4} className="product-lookup-title">
            Product Lookup
          </Title>
          <hr></hr>
        </Col>
      </Row>
      <Row className="product-lookup-actions" justify="space-between" align="middle">
        <Space style={{ margin: '16px 0px' }}>
          <Text strong style={{ marginRight: 4 }}>Store:</Text>
          <Select
            value={searchStoreId}
            onChange={(value) => setSearchStoreId(isSelectableProductLookupStore(value) ? value : null)}
            options={storeOptions}
            style={{ minWidth: 160 }}
            placeholder="Select store"
            showSearch
            optionFilterProp="label"
          />
        </Space>
        <Space style={{ margin: '16px 0px' }}>
          <Button type="primary" icon={<SearchOutlined />} onClick={() => setModalVisible(true)}>
            {!isMobile && 'Search By Keycodes'}
          </Button>
          {isMobile && (
            <Button
              type="primary"
              icon={<QrcodeOutlined />}
              onClick={() => setIsScannerActive(true)}
            >
              {!isMobile && 'Scan Barcode'}
            </Button>
          )}
        </Space>
      </Row>
      {/* Keycode Modal */}
      <Modal
        title="Enter Keycode(s)"
        open={modalVisible}
        onCancel={handleModalClose}
        className="product-lookup-modal"
        footer={[
          <Button
            key="search"
            type="primary"
            onClick={handleSearchKeycode}
            disabled={!!dialogError}
          >
            Search
          </Button>,
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>
        ]}
      >
        <Input
          type="textarea"
          value={keycodeInput}
          onChange={handleKeycodeChange}
          onKeyDown={(e) => {
            // Allow control keys: backspace, delete, arrows, tab, etc.
            if (
              [
                'Backspace',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Delete',
                'Tab',
                'Enter'
              ].includes(e.key) ||
              e.ctrlKey ||
              e.metaKey // Allow copy, paste, select all etc.
            ) {
              return
            }

            // Allow digits and comma
            const isDigit = /^[0-9]$/.test(e.key)
            const isComma = e.key === ','
            const isSpace = e.key === ' '

            if (!isDigit && !isComma && !isSpace) {
              e.preventDefault()
            }
          }}
          rows={4}
          status={dialogError ? 'error' : ''}
          placeholder="Enter Keycode(s)"
          className="product-lookup-keycode-input"
        />
        {dialogError ? (
          <Text type="danger" className="product-lookup-error">
            {dialogError}
          </Text>
        ) : (
          <Text type="secondary" className="product-lookup-info">
            Maximum 4 keycodes are allowed.
          </Text>
        )}
      </Modal>

      {/* Product grid area with localized spinner */}
      <div style={{ position: 'relative', minHeight: '400px' }}>
        {isReloading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Spinner style={{ transform: 'scale(1.5)' }} />
          </div>
        )}

        {productData ? (
          <>
            {searchStoreId && (
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary">Showing SOH for: </Text>
                <Text strong>{getStoreNameById(searchStoreId)}</Text>
              </div>
            )}
            <Row
              gutter={[16, 16]}
              className="product-lookup-grid"
              style={{ opacity: isReloading ? 0.5 : 1 }}
            >
              {productData.map((product, index) => (
                <Col xs={24} sm={12} md={6} lg={6} key={index}>
                  <ProductCard product={product} loadingSOH={loadingSOH} />
                </Col>
              ))}
            </Row>
          </>
        ) : !isReloading ? (
          <div className="product-lookup-no-data">
            <div className="product-lookup-image-container">
              <img src={NoDataImage} alt="No data available" className="product-lookup-image" />
            </div>
          </div>
        ) : null}
      </div>

      {/* Add a spacer div to ensure content doesn't get hidden behind fixed footer */}
      <div className="footer-spacer" style={{ height: '60px' }}></div>
    </div>
  )
}

export default ProductLookup